'use client';

import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  connectedAccountsQueryKey,
  fetchConnectedAccountsClient,
} from '@/lib/publish/connectedAccountsClient';

/**
 * Cached read of `GET /api/connected-accounts` for publish surfaces and the accounts strip.
 * Refetches on window focus, `visibilitychange` (visible), and `pageshow` (bfcache / OAuth return).
 */
export function useConnectedAccounts(enabled: boolean) {
  const query = useQuery({
    queryKey: connectedAccountsQueryKey,
    queryFn: fetchConnectedAccountsClient,
    enabled,
    staleTime: 0,
    gcTime: 5 * 60 * 1000,
    refetchOnWindowFocus: true,
    retry: 1,
  });

  const { refetch } = query;

  useEffect(() => {
    if (!enabled) return;
    const onPageshow = () => {
      void refetch();
    };
    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        void refetch();
      }
    };
    /** Phase MB.6 — OAuth return in same tab; debounce to avoid double fire with `refetchOnWindowFocus`. */
    let focusTimer: ReturnType<typeof setTimeout> | undefined;
    const onWindowFocus = () => {
      if (focusTimer) clearTimeout(focusTimer);
      focusTimer = setTimeout(() => {
        void refetch();
        focusTimer = undefined;
      }, 200);
    };
    window.addEventListener('pageshow', onPageshow);
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('focus', onWindowFocus);
    return () => {
      if (focusTimer) clearTimeout(focusTimer);
      window.removeEventListener('pageshow', onPageshow);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('focus', onWindowFocus);
    };
  }, [enabled, refetch]);

  return query;
}
